import { Product, Category } from '@pedix-workspace/utils';
import { Establishment } from '@pedix-workspace/utils';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { Inject, Injectable, Optional, RendererFactory2, inject } from '@angular/core';
import { REQUEST } from '../../../express.tokens';

import { EstablishmentUrlPipe, ImageStoragePipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { environment } from '../../../environments/environment';
import { AppRequestContextService } from '../../app-request-context.service';
import { DOCUMENT } from '@angular/common';

type FaviconType = { src: string; sizes?: string; type?: string };

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  siteName = 'Pedix';
  currentMetaTags: HTMLMetaElement[] = [];

  faviconsCreated = false;
  defaultFavicons: FaviconType[] = [
    { src: `/assets/icons/icon-48x48.png`, sizes: '48x48', type: 'image/png' },
    { src: `/assets/icons/icon-96x96.png`, sizes: '96x96', type: 'image/png' },
    { src: `/assets/icons/icon-144x144.png`, sizes: '144x144', type: 'image/png' },
  ];

  get defaultImage() {
    return `${environment.siteUrl}/assets/icons/icon-192x192.png`;
  }

  get establishment() {
    return this.appRequestContextService.establishment;
  }

  private request = inject(REQUEST, { optional: true });

  private rendererFactory = inject(RendererFactory2);
  private document = inject(DOCUMENT);
  private title = inject(Title);
  private meta = inject(Meta);
  private appRequestContextService = inject(AppRequestContextService);
  private establishmentUrlPipe = inject(EstablishmentUrlPipe);
  private imageStoragePipe = inject(ImageStoragePipe);

  resetCurrentMetaTags() {
    this.currentMetaTags.map(metaTag => {
      this.meta.removeTagElement(metaTag);
    });
  }

  setDefaultMetadata() {
    this.resetCurrentMetaTags();

    if (this.establishment) {
      this.setEstablishmentMetadata(this.establishment);
    } else {
      this.setMetadata({ name: 'author', content: this.siteName });
      this.setMetadata({ name: 'description', content: 'Catálogo online y pedidos por Whatsapp.' });
      this.setMetadata({ property: 'og:title', content: this.siteName });
      this.setMetadata({
        property: 'og:description',
        content: 'Catálogo online y pedidos por Whatsapp.',
      });
      this.setMetadata({ property: 'og:site_name', content: this.siteName });
      this.setMetadata({ property: 'og:url', content: environment.siteUrl });
      this.setMetadata({ property: 'og:type', content: 'website' });
      this.setMetadata({ property: 'og:title', content: this.siteName });
      this.setMetadata({ property: 'og:image', itemprop: 'image', content: this.defaultImage });
      this.setMetadata({ property: 'og:image:alt', content: this.siteName });
      this.setMetadata({ property: 'twitter:card', content: 'summary_large_image' });
    }
  }

  setEstablishmentMetadata(
    establishment: Pick<
      Establishment,
      | 'name'
      | 'slug'
      | 'logo'
      | 'seoSocialMedia'
      | 'customDomain'
      | 'subdomainRedirection'
      | 'subdomainStatus'
    >,
  ) {
    this.resetCurrentMetaTags();

    this.title.setTitle(establishment.name);

    this.setMetadata({ name: 'author', content: this.siteName });
    this.setMetadata({ property: 'og:site_name', content: this.siteName });
    this.setMetadata({ property: 'twitter:card', content: 'summary_large_image' });
    this.setMetadata({ property: 'twitter:title', content: establishment.name });
    this.setMetadata({
      property: 'og:url',
      content: this.establishmentUrlPipe.transform(establishment),
    });
    this.setMetadata({ property: 'og:type', content: 'website' });
    if (this.establishment.features.includes('WHITE_LABEL_BRANDING')) {
      this.setMetadata({ property: 'og:title', content: establishment.name });
    } else {
      this.setMetadata({
        property: 'og:title',
        content: `${this.siteName} | ${establishment.name}`,
      });
    }
    this.setMetadata({ property: 'og:image:alt', content: establishment.name });
    this.setMetadata({ property: 'twitter:image:alt', content: establishment.name });

    // { property: 'twitter:creator', content: '@urbn.catering' },

    // { property: 'twitter:site', content: '@pedixapp' },

    // { property: 'og:image:type', content: 'image/jpeg' },

    // { property: 'og:image:type', content: 'image/jpeg' },
    // { property: 'og:image:width', content: '1200' },
    // { property: 'og:image:height', content: '630' },

    if (!establishment.seoSocialMedia) {
      let logo = this.defaultImage;

      if (establishment.logo && this.establishment.features.includes('SEO_SOCIAL_MEDIA')) {
        logo = this.imageStoragePipe.transform(establishment.logo, { size: '250x250' });
      }

      this.setMetadata({ property: 'og:image', itemprop: 'image', content: logo });

      this.setFavicons(this.defaultFavicons);
    } else {
      const userAgent = this.request?.headers['user-agent'] || '';
      const isWhatsAppCrawler = userAgent.includes('WhatsApp/');

      if (establishment.seoSocialMedia?.favicon) {
        this.setFavicons([{ src: establishment.seoSocialMedia.favicon }]);
      } else {
        this.setFavicons(this.defaultFavicons);
      }

      const mediaImage = isWhatsAppCrawler
        ? establishment.seoSocialMedia?.shortImage
        : establishment.seoSocialMedia?.image;
      const socialMediaImage =
        this.imageStoragePipe.transform(mediaImage || establishment.logo, { size: '250x250' }) ||
        this.defaultImage;
      const linkImage =
        this.imageStoragePipe.transform(mediaImage || establishment.logo, { size: '250x250' }) ||
        this.defaultImage;
      const description =
        establishment.seoSocialMedia?.description || establishment.seoSocialMedia?.shortDescription;
      const shortDescription =
        establishment.seoSocialMedia?.shortDescription || establishment.seoSocialMedia?.description;

      if (description) {
        this.setMetadata({ name: 'description', content: description });
      }
      if (establishment.seoSocialMedia?.keywords) {
        this.setMetadata({
          name: 'keywords',
          content: establishment.seoSocialMedia.keywords.join(', '),
        });
      }

      if (shortDescription) {
        this.setMetadata({ property: 'twitter:description', content: shortDescription });
        this.setMetadata({ property: 'og:description', content: shortDescription });
      }
      if (socialMediaImage) {
        this.setMetadata({ property: 'twitter:image', content: socialMediaImage });
        this.setMetadata({ property: 'og:image', content: socialMediaImage });
        this.setMetadata({ property: 'og:image:url', content: socialMediaImage });
      }
      if (linkImage) {
        this.setMetadata({ property: 'og:image', itemprop: 'image', content: linkImage });
      }
    }
  }

  setCategoryMetadata(
    category: Pick<Category, 'id' | 'name' | 'image'>,
    establishment: Pick<
      Establishment,
      | 'name'
      | 'slug'
      | 'logo'
      | 'seoSocialMedia'
      | 'customDomain'
      | 'subdomainRedirection'
      | 'subdomainStatus'
    >,
  ) {
    this.resetCurrentMetaTags();

    const pageTitle = `${establishment.name} | ${category.name}`;

    this.title.setTitle(pageTitle);

    this.setMetadata({ name: 'author', content: this.siteName });
    this.setMetadata({ property: 'og:site_name', content: this.siteName });
    this.setMetadata({ property: 'twitter:card', content: 'summary_large_image' });
    this.setMetadata({ property: 'twitter:title', content: pageTitle });
    this.setMetadata({
      property: 'og:url',
      content: this.establishmentUrlPipe.transform(establishment, ['categoria', category.id]),
    });
    this.setMetadata({ property: 'og:type', content: 'website' });

    if (this.establishment.features.includes('WHITE_LABEL_BRANDING')) {
      this.setMetadata({ property: 'og:title', content: pageTitle });
    } else {
      this.setMetadata({ property: 'og:title', content: `${this.siteName} | ${pageTitle}` });
    }
    this.setMetadata({ property: 'og:image:alt', content: category.name });
    this.setMetadata({ property: 'twitter:image:alt', content: category.name });

    const socialMediaImage =
      this.imageStoragePipe.transform(category.image || establishment.logo, {
        size: '1500x1500',
      }) || this.defaultImage;
    const linkImage =
      this.imageStoragePipe.transform(category.image || establishment.logo, { size: '250x250' }) ||
      this.defaultImage;
    const description =
      establishment.seoSocialMedia?.description || establishment.seoSocialMedia?.shortDescription;
    const shortDescription =
      establishment.seoSocialMedia?.shortDescription || establishment.seoSocialMedia?.description;

    if (description) {
      this.setMetadata({ name: 'description', content: description });
    }
    if (shortDescription) {
      this.setMetadata({ property: 'twitter:description', content: shortDescription });
      this.setMetadata({ property: 'og:description', content: shortDescription });
    }
    if (establishment.seoSocialMedia?.keywords) {
      this.setMetadata({
        name: 'keywords',
        content: establishment.seoSocialMedia.keywords.join(', '),
      });
    }
    if (socialMediaImage) {
      this.setMetadata({ property: 'twitter:image', content: socialMediaImage });
      this.setMetadata({ property: 'og:image', content: socialMediaImage });
      this.setMetadata({ property: 'og:image:url', content: socialMediaImage });
    }
    if (linkImage) {
      this.setMetadata({ property: 'og:image', itemprop: 'image', content: linkImage });
    }
  }

  setProductMetadata(
    product: Pick<Product, 'id' | 'name' | 'description' | 'images'>,
    establishment: Pick<
      Establishment,
      | 'name'
      | 'slug'
      | 'logo'
      | 'seoSocialMedia'
      | 'customDomain'
      | 'subdomainRedirection'
      | 'subdomainStatus'
    >,
  ) {
    this.resetCurrentMetaTags();

    const pageTitle = `${establishment.name} | ${product.name}`;

    this.title.setTitle(pageTitle);

    this.setMetadata({ name: 'author', content: this.siteName });
    this.setMetadata({ property: 'og:site_name', content: this.siteName });
    this.setMetadata({ property: 'twitter:card', content: 'summary_large_image' });
    this.setMetadata({ property: 'twitter:title', content: pageTitle });
    this.setMetadata({
      property: 'og:url',
      content: this.establishmentUrlPipe.transform(establishment, ['producto', product.id]),
    });
    this.setMetadata({ property: 'og:type', content: 'website' });
    if (this.establishment.features.includes('WHITE_LABEL_BRANDING')) {
      this.setMetadata({ property: 'og:title', content: pageTitle });
    } else {
      this.setMetadata({ property: 'og:title', content: `${this.siteName} | ${pageTitle}` });
    }
    this.setMetadata({ property: 'og:image:alt', content: product.name });
    this.setMetadata({ property: 'twitter:image:alt', content: product.name });

    const socialMediaImage =
      this.imageStoragePipe.transform(product.images[0] || establishment.logo, {
        size: '1500x1500',
      }) || this.defaultImage;
    const linkImage =
      this.imageStoragePipe.transform(product.images[0] || establishment.logo, {
        size: '250x250',
      }) || this.defaultImage;

    if (product.description) {
      this.setMetadata({ name: 'description', content: product.description });
      this.setMetadata({ property: 'twitter:description', content: product.description });
      this.setMetadata({ property: 'og:description', content: product.description });
    }
    if (establishment.seoSocialMedia?.keywords) {
      this.setMetadata({
        name: 'keywords',
        content: establishment.seoSocialMedia.keywords.join(', '),
      });
    }
    if (socialMediaImage) {
      this.setMetadata({ property: 'twitter:image', content: socialMediaImage });
      this.setMetadata({ property: 'og:image', content: socialMediaImage });
      this.setMetadata({ property: 'og:image:url', content: socialMediaImage });
    }
    if (linkImage) {
      this.setMetadata({ property: 'og:image', itemprop: 'image', content: linkImage });
    }
  }

  private setMetadata(metaDefinition: MetaDefinition) {
    const metaTag = this.meta.addTag(metaDefinition);

    this.currentMetaTags.push(metaTag);
  }

  private setFavicons(favicons: FaviconType[]) {
    if (this.faviconsCreated) {
      return;
    }
    const renderer = this.rendererFactory.createRenderer(null, null);

    favicons.forEach(favicon => {
      const link = renderer.createElement('link');

      renderer.setAttribute(link, 'rel', 'icon');
      renderer.setAttribute(link, 'href', favicon.src);

      if (favicon.sizes) {
        renderer.setAttribute(link, 'sizes', favicon.sizes);
      }
      if (favicon.type) {
        renderer.setAttribute(link, 'type', favicon.type);
      }

      renderer.appendChild(this.document.head, link);
    });

    this.faviconsCreated = true;
  }
}
